// src/lib/shipping/shippingStore.tsx
//
// Reactive store for the agent-driven vendor/shipping pipeline.
//
// State machine (per shipment):
//
//   customer_order_submitted
//     -> po_sent                     (WhatsApp out to vendor)
//     -> pfi_received                (vendor replied with PFI)
//     -> ready_to_pay                (parsed, console card #1)
//     -> payment_proof_captured      (human sent, console card #2)
//     -> vendor_tracking_received    (vendor shipped, DHL / FedEx #)
//     -> ready_to_dispatch            (console card #3, await click)
//     -> gls_label_created           (operator clicked, label out)
//     -> in_transit                  (GLS webhook, in_transit)
//     -> out_for_delivery             (GLS webhook, OOD)
//     -> delivered                   (GLS webhook, delivered)
//     -> reconciled                  (human click on bank statement)
//
// Persisted to localStorage under 'bench-shipments-v1'. Schema is
// shape-checked on read so the demo survives schema evolution.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";

const STORAGE_KEY = "bench-shipments-v1";

// ---------- public types ----------

export type ShipmentStatus =
  | "customer_order_submitted"
  | "po_sent"
  | "pfi_received"
  | "ready_to_pay"
  | "payment_proof_captured"
  | "vendor_tracking_received"
  | "ready_to_dispatch"
  | "gls_label_created"
  | "in_transit"
  | "out_for_delivery"
  | "delivered"
  | "reconciled";

export interface ShipmentTimelineEntry {
  /** ISO timestamp */
  ts: string;
  status: ShipmentStatus;
  /** Free-form human/agent note for the audit feed */
  note: string;
  actor: "system" | "agent" | "human";
}

export interface Shipment {
  id: string;
  customerId: string;
  customerName: string;
  shipTo: string[];
  vendor: string;
  poId: string;
  /** Lines use the SKU + qty shape from DEMO_ORDERS for visual consistency. */
  lines: Array<{ sku: string; name: string; qty: number; unitPriceEUR: number }>;
  status: ShipmentStatus;
  /** Deterministic reference token, rendered on every PFI. */
  referenceToken: string;
  amountCents: number;
  vendorTrackingNumber?: string;
  vendorTrackingCarrier?: "DHL" | "FedEx";
  glsTrackingNumber?: string;
  glsServiceLevel?: "standard" | "express" | "saturday";
  timeline: ShipmentTimelineEntry[];
}

export const STATUS_LABEL: Record<ShipmentStatus, string> = {
  customer_order_submitted: "Order submitted",
  po_sent: "PO sent",
  pfi_received: "PFI received",
  ready_to_pay: "Ready to pay",
  payment_proof_captured: "Payment proof captured",
  vendor_tracking_received: "Vendor shipped",
  ready_to_dispatch: "Ready to dispatch",
  gls_label_created: "GLS label created",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  reconciled: "Reconciled",
};

/** Total ordering for the status pill / progress bar. */
export const STATUS_ORDER: ShipmentStatus[] = [
  "customer_order_submitted",
  "po_sent",
  "pfi_received",
  "ready_to_pay",
  "payment_proof_captured",
  "vendor_tracking_received",
  "ready_to_dispatch",
  "gls_label_created",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "reconciled",
];

// ---------- demo seed ----------

const SEED: Shipment[] = [
  {
    id: "SHP-0001",
    customerId: "C-1001",
    customerName: "Studio Norden",
    shipTo: [
      "Studio Norden",
      "Prinsengracht 263",
      "1016 GV Amsterdam",
      "Netherlands",
    ],
    vendor: "Epson EU",
    poId: "PO-0421",
    lines: [
      { sku: "EPSON-EH-LS800", name: "Epson EH-LS800 Projector", qty: 1, unitPriceEUR: 2499 },
    ],
    status: "delivered",
    referenceToken: "BENCH-0421-249900",
    amountCents: 2499 * 100,
    vendorTrackingNumber: "DHL-1Z999AA1",
    vendorTrackingCarrier: "DHL",
    glsTrackingNumber: "GLS-3400921184",
    glsServiceLevel: "express",
    timeline: [
      { ts: "2026-07-08T09:00:00Z", status: "customer_order_submitted", note: "Studio Norden ordered Epson EH-LS800.", actor: "system" },
      { ts: "2026-07-08T09:02:14Z", status: "po_sent", note: "WhatsApp template po_order sent.", actor: "agent" },
      { ts: "2026-07-08T11:42:01Z", status: "pfi_received", note: "Epson replied with PDF PFI.", actor: "agent" },
      { ts: "2026-07-08T11:42:55Z", status: "ready_to_pay", note: "OCR parsed: €2,499.00 / BENCH-0421-249900", actor: "agent" },
      { ts: "2026-07-08T13:18:30Z", status: "payment_proof_captured", note: "Human dropped SWIFT XML screenshot.", actor: "human" },
      { ts: "2026-07-09T07:55:12Z", status: "vendor_tracking_received", note: "DHL 1Z999AA1 issued.", actor: "agent" },
      { ts: "2026-07-10T08:30:00Z", status: "ready_to_dispatch", note: "Stock arrived at BENCHMARK.", actor: "system" },
      { ts: "2026-07-10T08:42:11Z", status: "gls_label_created", note: "Operator clicked Print GLS label. Service: express.", actor: "human" },
      { ts: "2026-07-10T11:00:00Z", status: "in_transit", note: "GLS pickup confirmed.", actor: "agent" },
      { ts: "2026-07-11T07:15:00Z", status: "out_for_delivery", note: "GLS webhook: out_for_delivery.", actor: "agent" },
      { ts: "2026-07-11T14:20:00Z", status: "delivered", note: "GLS webhook: delivered, Anders Holmlund signed.", actor: "agent" },
      { ts: "2026-07-11T18:00:00Z", status: "reconciled", note: "Bank statement checked, click recorded.", actor: "human" },
    ],
  },
  {
    id: "SHP-0002",
    customerId: "C-1002",
    customerName: "Cinema Royale",
    shipTo: [
      "Cinema Royale",
      "47, rue de Richelieu",
      "75001 Paris",
      "France",
    ],
    vendor: "madVR Labs",
    poId: "PO-0422",
    lines: [
      { sku: "MADVR-ENVY-MK2", name: "madVR Envy MK2 Video Processor", qty: 1, unitPriceEUR: 12990 },
    ],
    status: "in_transit",
    referenceToken: "BENCH-0422-1299000",
    amountCents: 12990 * 100,
    vendorTrackingNumber: "FEDEX-7729-1183",
    vendorTrackingCarrier: "FedEx",
    glsTrackingNumber: "GLS-3400921185",
    glsServiceLevel: "standard",
    timeline: [
      { ts: "2026-07-09T09:15:00Z", status: "customer_order_submitted", note: "Cinema Royale ordered madVR Envy MK2.", actor: "system" },
      { ts: "2026-07-09T09:17:02Z", status: "po_sent", note: "WhatsApp template po_order sent.", actor: "agent" },
      { ts: "2026-07-09T11:30:00Z", status: "pfi_received", note: "madVR replied with PDF PFI.", actor: "agent" },
      { ts: "2026-07-09T11:30:40Z", status: "ready_to_pay", note: "OCR parsed: €12,990.00 / BENCH-0422-1299000", actor: "agent" },
      { ts: "2026-07-09T13:45:00Z", status: "payment_proof_captured", note: "Human captured bank confirmation.", actor: "human" },
      { ts: "2026-07-10T08:00:00Z", status: "vendor_tracking_received", note: "FedEx 7729-1183 issued.", actor: "agent" },
      { ts: "2026-07-10T15:00:00Z", status: "ready_to_dispatch", note: "Stock at BENCHMARK.", actor: "system" },
      { ts: "2026-07-10T15:10:00Z", status: "gls_label_created", note: "Operator printed GLS label. Service: standard.", actor: "human" },
      { ts: "2026-07-11T09:30:00Z", status: "in_transit", note: "GLS pickup confirmed.", actor: "agent" },
    ],
  },
];

// ---------- storage helpers ----------

function loadFromStorage(): Shipment[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (s) =>
          typeof s === "object" &&
          s &&
          "id" in s &&
          "status" in s &&
          "timeline" in s,
      )
    ) {
      return parsed as Shipment[];
    }
    return null;
  } catch {
    return null;
  }
}

function saveToStorage(rows: Shipment[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore quota / private-mode
  }
}

// ---------- minting ----------

/** Allocate next SHP-xxxx id past whatever is already in the set. */
function nextShipmentId(rows: Shipment[]): string {
  let max = 0;
  for (const r of rows) {
    const m = /^SHP-(\d+)$/.exec(r.id);
    if (m && m[1]) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `SHP-${String(max + 1).padStart(4, "0")}`;
}

function nextPoId(rows: Shipment[]): string {
  let max = 0;
  for (const r of rows) {
    const m = /^PO-(\d+)$/.exec(r.poId);
    if (m && m[1]) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `PO-${max + 1}`;
}

// ---------- e2e demo runner ----------

export interface DemoSpawnInput {
  customerId: string;
  customerName: string;
  shipTo: string[];
  /** SKU from DEMO_PRODUCTS */
  sku: string;
  /** Product display name */
  productName: string;
  /** Wholesale price per unit (EUR) */
  unitPriceEUR: number;
  qty: number;
}

/**
 * Spawn a new shipment and walk it through every state in one tick
 * (with realistic timestamps). The UI then sits at "delivered" — a
 * reviewer sees the full audit trail in one click of "Run end-to-end demo".
 */
export function spawnE2EDemo(
  rows: Shipment[],
  input: DemoSpawnInput,
  now: Date = new Date(),
): Shipment {
  const id = nextShipmentId(rows);
  const poId = nextPoId(rows);
  const amount = input.unitPriceEUR * input.qty;
  const amountCents = amount * 100;
  const referenceToken = `BENCH-${poId.replace(/^PO-/, "")}-${amountCents}`;
  const created: Shipment = {
    id,
    customerId: input.customerId,
    customerName: input.customerName,
    shipTo: input.shipTo,
    vendor: "Epson EU",
    poId,
    lines: [
      {
        sku: input.sku,
        name: input.productName,
        qty: input.qty,
        unitPriceEUR: input.unitPriceEUR,
      },
    ],
    status: "customer_order_submitted",
    referenceToken,
    amountCents,
    timeline: [
      {
        ts: now.toISOString(),
        status: "customer_order_submitted",
        note: `${input.customerName} ordered ${input.productName} (qty ${input.qty}).`,
        actor: "system",
      },
    ],
  };

  // Walk the state machine forward. Each step is a "+1 minute" from `now`.
  const step: Array<{ status: ShipmentStatus; note: string; actor: ShipmentTimelineEntry["actor"]; setters?: Partial<Shipment> }> = [
    {
      status: "po_sent",
      note: "WhatsApp template po_order sent.",
      actor: "agent",
    },
    {
      status: "pfi_received",
      note: `${"Epson EU"} replied with PDF PFI.`,
      actor: "agent",
    },
    {
      status: "ready_to_pay",
      note: `OCR parsed: ${amount.toLocaleString("en-GB", { style: "currency", currency: "EUR" })} / ${referenceToken}`,
      actor: "agent",
    },
    {
      status: "payment_proof_captured",
      note: "Human captured bank confirmation (SWIFT XML screenshot).",
      actor: "human",
    },
    {
      status: "vendor_tracking_received",
      note: "DHL 1Z999AA2 issued by Epson EU.",
      actor: "agent",
      setters: { vendorTrackingNumber: "DHL-1Z999AA2", vendorTrackingCarrier: "DHL" },
    },
    {
      status: "ready_to_dispatch",
      note: "Stock arrived at BENCHMARK warehouse.",
      actor: "system",
    },
    {
      status: "gls_label_created",
      note: "Operator clicked 'Print GLS label'. Service: express.",
      actor: "human",
      setters: { glsTrackingNumber: "GLS-3400921186", glsServiceLevel: "express" },
    },
    {
      status: "in_transit",
      note: "GLS pickup confirmed via webhook.",
      actor: "agent",
    },
    {
      status: "out_for_delivery",
      note: "GLS webhook: out_for_delivery.",
      actor: "agent",
    },
    {
      status: "delivered",
      note: `GLS webhook: delivered, ${input.customerName} signed.`,
      actor: "agent",
    },
    {
      status: "reconciled",
      note: "Bank statement checked, mark-as-reconciled recorded.",
      actor: "human",
    },
  ];

  let cursor = now.getTime();
  let result = created;
  for (const s of step) {
    cursor += 60_000;
    const entry: ShipmentTimelineEntry = {
      ts: new Date(cursor).toISOString(),
      status: s.status,
      note: s.note,
      actor: s.actor,
    };
    result = {
      ...result,
      ...(s.setters ?? {}),
      status: s.status,
      timeline: [...result.timeline, entry],
    };
  }
  return result;
}

// ---------- reducer ----------

export type ShipmentAction =
  | { type: "replaceAll"; payload: Shipment[] }
  | { type: "reset" }
  | { type: "spawn"; payload: Shipment }
  | { type: "advance"; payload: { id: string; next: ShipmentStatus; note: string; actor: ShipmentTimelineEntry["actor"]; setters?: Partial<Shipment> | undefined } };

function reducer(rows: Shipment[], action: ShipmentAction): Shipment[] {
  switch (action.type) {
    case "replaceAll":
      return action.payload;
    case "reset":
      return SEED;
    case "spawn":
      // Newest first.
      return [action.payload, ...rows];
    case "advance": {
      return rows.map((r) => {
        if (r.id !== action.payload.id) return r;
        const entry: ShipmentTimelineEntry = {
          ts: new Date().toISOString(),
          status: action.payload.next,
          note: action.payload.note,
          actor: action.payload.actor,
        };
        return {
          ...r,
          ...(action.payload.setters ?? {}),
          status: action.payload.next,
          timeline: [...r.timeline, entry],
        };
      });
    }
  }
}

// ---------- context ----------

export interface ShippingContextValue {
  shipments: Shipment[];
  getById: (id: string) => Shipment | undefined;
  spawnDemo: (input: DemoSpawnInput) => Shipment;
  advance: (
    id: string,
    next: ShipmentStatus,
    note: string,
    actor: ShipmentTimelineEntry["actor"],
    setters?: Partial<Shipment>,
  ) => void;
  reset: () => void;
}

const ShippingContext = createContext<ShippingContextValue | null>(null);

export function ShippingProvider({ children }: { children: ReactNode }) {
  const [rows, dispatch] = useReducer(
    reducer,
    undefined,
    () => loadFromStorage() ?? SEED,
  );

  useEffect(() => {
    saveToStorage(rows);
  }, [rows]);

  const value = useMemo<ShippingContextValue>(
    () => ({
      shipments: rows,
      getById: (id) => rows.find((r) => r.id === id),
      spawnDemo: (input) => {
        const created = spawnE2EDemo(rows, input);
        dispatch({ type: "spawn", payload: created });
        return created;
      },
      advance: (id, next, note, actor, setters) =>
        dispatch({ type: "advance", payload: { id, next, note, actor, setters } }),
      reset: () => dispatch({ type: "reset" }),
    }),
    [rows],
  );

  return (
    <ShippingContext.Provider value={value}>{children}</ShippingContext.Provider>
  );
}

export function useShipping(): ShippingContextValue {
  const ctx = useContext(ShippingContext);
  if (!ctx) {
    throw new Error(
      "useShipping must be used inside a <ShippingProvider />",
    );
  }
  return ctx;
}

export function _resetShipmentsStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
